/* Header state is shared by every storefront page. */
(() => {
  let headerFrame = 0;
  const syncAnnouncementOffset = () => {
    headerFrame = 0;
    let bottom = 0;
    document.querySelectorAll('.shopify-section-announcement-bar').forEach(bar => {
      if (!bar.getClientRects().length) return;
      const rect = bar.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom > 0) bottom = Math.max(bottom, rect.bottom);
    });
    document.documentElement.style.setProperty('--pg-announcement-offset', `${Math.max(0, bottom)}px`);
  };
  const queueHeaderOffset = () => {
    if (!headerFrame) headerFrame = requestAnimationFrame(syncAnnouncementOffset);
  };
  const update = () => {
    document.body.classList.toggle('pg-scrolled', window.scrollY > 60);
    queueHeaderOffset();
  };
  window.addEventListener('scroll', update, {passive: true});
  window.addEventListener('resize', queueHeaderOffset, {passive: true});
  const announcementObserver = new ResizeObserver(queueHeaderOffset);
  const observeAnnouncements = () => {
    announcementObserver.disconnect();
    document.querySelectorAll('.shopify-section-announcement-bar').forEach(bar => announcementObserver.observe(bar));
    queueHeaderOffset();
  };
  observeAnnouncements();
  document.addEventListener('shopify:section:load', observeAnnouncements);
  document.addEventListener('shopify:section:unload', observeAnnouncements);
  document.addEventListener('shopify:section:reorder', queueHeaderOffset);
  syncAnnouncementOffset();
  update();
  // Parent labels navigate; their caret and hover retain submenu access.
  const menuSelector = '.header__inline-menu dropdown-menu, .header__inline-menu mega-menu';
  const closeMenus = () => document.querySelectorAll(menuSelector).forEach(menu => menu.close?.());
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-pg-menu-toggle]');
    if (trigger && (event.target.closest('.icon-caret') || !trigger.getAttribute('href') || trigger.getAttribute('href') === '#')) {
      event.preventDefault();
      const menu = trigger.closest('dropdown-menu, mega-menu');
      menu?.handleMouseover?.();
    } else if (!event.target.closest('.header__inline-menu')) closeMenus();
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const menu = event.target.closest('dropdown-menu, mega-menu');
    if (!menu) return;
    event.preventDefault();
    menu.querySelector(':scope > [data-pg-menu-toggle]')?.focus();
    closeMenus();
  });
  document.addEventListener('focusin', event => {
    if (!event.target.closest('.header__inline-menu')) closeMenus();
  });
  const bindHeader = () => {
    const header = document.querySelector('.header__inline-menu')?.closest('.shopify-section');
    if (!header || header.pgMenuObserver) return;
    const sync = menu => {
      const button = menu.querySelector(':scope > [data-pg-menu-toggle]');
      if (button) button.setAttribute('aria-expanded', String(menu.classList.contains('is-active')));
    };
    header.pgMenuObserver = new MutationObserver(records => records.forEach(({target}) => {
      if (target.matches('dropdown-menu, mega-menu')) sync(target);
    }));
    header.pgMenuObserver.observe(header, {attributes:true, attributeFilter:['class'], subtree:true});
    header.querySelectorAll('dropdown-menu, mega-menu').forEach(sync);
  };
  bindHeader();
  document.addEventListener('shopify:section:load', bindHeader);
  document.addEventListener('shopify:section:unload', event => {
    event.target.pgMenuObserver?.disconnect();
    delete event.target.pgMenuObserver;
  });
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-pg-concierge-topic]');
    if (!link) return;
    const select = document.querySelector('[data-pg-concierge-select]');
    if (select && [...select.options].some(option => option.value === link.dataset.pgConciergeTopic)) {
      select.value = link.dataset.pgConciergeTopic;
      select.dispatchEvent(new Event('change', {bubbles:true}));
    }
  });
})();
/* One anchor controller for every storefront page, including full same-page URLs. */
(() => {
  if (window.PuraGoldAnchors) return;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let scrollToken = 0;
  const normalizePath = path => path.replace(/\/+$/, '') || '/';
  const targetFromHash = hash => {
    if (!hash || hash === '#') return null;
    try { return document.getElementById(decodeURIComponent(hash.slice(1))); }
    catch (_) { return null; }
  };
  const headerOffset = () => {
    let bottom = 0;
    document.querySelectorAll('sticky-header, .section-header, .shopify-section-header, #shopify-section-header, .header-wrapper').forEach(header => {
      const rect = header.getBoundingClientRect();
      const position = getComputedStyle(header).position;
      if (rect.bottom > 0 && (position === 'fixed' || position === 'sticky' || rect.top <= 1)) bottom = Math.max(bottom, rect.bottom);
    });
    return Math.max(0, Math.round(bottom)) + 16;
  };
  const focusTarget = target => {
    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
      target.addEventListener('blur', () => target.removeAttribute('tabindex'), {once:true});
    }
    target.focus({preventScroll:true});
  };
  const settlePassedEntrances = (target, destination) => {
    if (!window.ScrollTrigger?.getAll || destination <= window.scrollY + window.innerHeight * .5) return;
    const start = window.scrollY;
    window.ScrollTrigger.getAll().forEach(trigger => {
      const triggerElement = trigger.trigger;
      if (!trigger.vars?.once || !trigger.animation || !triggerElement || target === triggerElement || target.contains(triggerElement) || triggerElement.contains(target)) return;
      if (trigger.start <= start || trigger.start >= destination) return;
      trigger.animation.progress(1, true);
      trigger.kill(false);
    });
    window.ScrollTrigger.update();
  };
  const scrollToTarget = (target, options = {}) => {
    if (!target) return false;
    if (target.matches('details') && !target.open) target.open = true;
    const offset = headerOffset();
    const immediate = reducedMotion.matches || options.immediate;
    const token = ++scrollToken;
    const destination = Math.max(0, target.getBoundingClientRect().top + window.scrollY - offset);
    settlePassedEntrances(target, destination);
    if (window.PuraGoldLenis?.scrollTo) {
      window.PuraGoldLenis.scrollTo(target, {offset: -offset, immediate, duration: immediate ? 0 : 1.05});
    } else {
      window.scrollTo({top: destination, behavior: immediate ? 'auto' : 'smooth'});
      if (!immediate) window.setTimeout(() => {
        if (token !== scrollToken || !target.isConnected) return;
        const correction = target.getBoundingClientRect().top - headerOffset();
        if (Math.abs(correction) > 2) window.scrollTo({top: Math.max(0, window.scrollY + correction), behavior:'auto'});
      }, 1100);
    }
    focusTarget(target);
    return true;
  };
  ['wheel', 'touchstart', 'pointerdown'].forEach(type => window.addEventListener(type, () => { scrollToken += 1; }, {passive:true}));
  const updateHistory = hash => {
    const url = `${location.pathname}${location.search}${hash}`;
    if (location.hash === hash) history.replaceState(null, '', url);
    else history.pushState(null, '', url);
  };
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented || event.button > 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || link.target === '_blank' || link.hasAttribute('download')) return;
    let url;
    try { url = new URL(link.href, location.href); } catch (_) { return; }
    if (url.origin !== location.origin || normalizePath(url.pathname) !== normalizePath(location.pathname)) return;
    const target = targetFromHash(url.hash);
    if (!target) return;
    if (target.closest('.customer.login') && (url.hash === '#recover' || url.hash === '#login')) return;
    event.preventDefault();
    updateHistory(url.hash);
    scrollToTarget(target);
  });
  window.addEventListener('hashchange', () => scrollToTarget(targetFromHash(location.hash)));
  const alignInitialHash = () => {
    const target = targetFromHash(location.hash);
    if (target) requestAnimationFrame(() => requestAnimationFrame(() => scrollToTarget(target, {immediate:true})));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', alignInitialHash, {once:true});
  else alignInitialHash();
  window.PuraGoldAnchors = {scrollTo: scrollToTarget};
})();
/* Shared pointer effect: one instance on every storefront page. */
(() => {
 const media=matchMedia('(hover:hover) and (pointer:fine) and (prefers-reduced-motion:no-preference)');
 let cleanup=()=>{};
 const mount=()=>{
  cleanup();if(!media.matches)return;
  const dot=document.createElement('div');dot.className='pg-global-cursor ignore-empty';dot.setAttribute('aria-hidden','true');document.body.append(dot);
  const controller=new AbortController(),signal=controller.signal;let frame=0,x=0,y=0;
  document.addEventListener('pointermove',e=>{if(e.pointerType==='touch')return;x=e.clientX;y=e.clientY;dot.style.opacity='1';document.documentElement.classList.add('pg-custom-cursor-active');if(!frame)frame=requestAnimationFrame(()=>{dot.style.transform=`translate3d(${x-22}px,${y-22}px,0)`;frame=0;});},{signal,passive:true});
  document.addEventListener('pointerover',e=>dot.classList.toggle('is-interactive',!!e.target.closest('a,button,summary,[role="button"]')),{signal,passive:true});
  document.addEventListener('pointerdown',()=>dot.classList.add('is-pressed'),{signal,passive:true});
  document.addEventListener('pointerup',()=>dot.classList.remove('is-pressed'),{signal,passive:true});
  document.addEventListener('pointercancel',()=>dot.classList.remove('is-pressed'),{signal,passive:true});
  const hide=()=>{document.documentElement.classList.remove('pg-custom-cursor-active');dot.style.opacity='0';dot.classList.remove('is-pressed');};document.addEventListener('pointerleave',hide,{signal});window.addEventListener('blur',hide,{signal});
  cleanup=()=>{hide();controller.abort();cancelAnimationFrame(frame);dot.remove();};
 };
 media.addEventListener('change',mount);mount();
})();
